import {
  getUploadImageSize,
  UPLOAD_IMAGE_JPEG_QUALITY,
  UPLOAD_IMAGE_MAX_BYTES,
} from '@griply/utils';
import { getErrorReporter } from 'shared/services/error-tracking';

const JPEG_TYPE = 'image/jpeg';
// JPEG has no alpha: without a backdrop, transparent pixels encode as black.
const JPEG_BACKDROP = '#ffffff';

const encodeJpeg = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error('Could not encode the image as JPEG')),
      JPEG_TYPE,
      UPLOAD_IMAGE_JPEG_QUALITY,
    ),
  );

// A file under the Storage size rule is uploaded as it is, keeping its format
// (transparency, animation). One over it would be denied, so it is downscaled and
// re-encoded as JPEG, like mobile's prepareImageForUpload does for every upload.
// A file the browser cannot decode (TIFF in Chromium) is reported and left as it
// is, so the upload fails the way it did before rather than silently.
export const prepareImageForUpload = async (file: File): Promise<Blob> => {
  if (file.size < UPLOAD_IMAGE_MAX_BYTES) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = getUploadImageSize(bitmap);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      throw new Error('No 2d canvas context to resize the image');
    }

    context.fillStyle = JPEG_BACKDROP;
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    return await encodeJpeg(canvas);
  } catch (error: unknown) {
    getErrorReporter().captureException(error, {
      tags: { source: 'prepare-image-for-upload' },
      extra: { fileType: file.type, fileSize: file.size },
    });
    return file;
  }
};
